import { NgModule } from '@angular/core';
import { Routes, RouterModule, RouteReuseStrategy } from '@angular/router';

// API //
import { RoutesManager } from './shared/models/routes.interfaces';

// SERVICES //
import { CustomReuseStrategy } from './services/custom-reuse.strategy';
import { CustomPreloadingStrategy } from './services/custom-preloading.strategy';

// GUARDS //
import { HomeGuard } from './home/guard/home.guard';
import { UserRoleGuard } from './auth/user-role.guard';
import { FeatureFlagGuard } from './feature-flag/feature-flag.guard';
import { LeavePageGuard } from './shared/leave-page/leave-page.guard';
import { AuthGuard, CompanyVerificationGuard } from './auth/auth.guard';
import { DashboardRoleGuard } from './dashboard/dashboard-user-role.guard';
import { AssessmentsGuard } from './ai-assessments/guards/assessments.guard';
import { DsrUnderMaintenanceGuard } from './shared/under-maintenance/dsr-under-maintencance.guard';

// ENUM //
import { FeatureFlags } from './api/models/profile/profile-feature-flags.enum';
import { LookerDashboardTypeEnum } from './dashboard/models/dashboard.enums';
import { DASHBOARD_UUIDS } from './dashboard/models/dashboard-uuids';

// SHARED COMPONENTS //
import { ErrorPageComponent } from './shared/error-page/error-page.component';
import { ThankYouPageComponent } from './shared/thank-you-page/thank-you-page.component';
import { FullPathMatch, PrefixPathMatch } from './shared/models/routes.interfaces';
import { MineNoPermissionsComponent } from './shared/mine-no-permissions/mine-no-permissions.component';
import { PortalNoPermissionsComponent } from './shared/portal-no-permissions/portal-no-permissions.component';

// COMPONENTS //
import { HomeComponent } from './home/home.component';
import { DpoAdvisorComponent } from './dpo-advisor/dpo-advisor.component';
import { PostCheckoutComponent } from './core/post-checkout/post-checkout.component';
import { IntegrationOauthCallbackComponent } from './systems/system-page/system-request-handling/integration-oauth/integration-oauth-callback/integration-oauth-callback.component';
import { UnderMaintencanceComponent } from './shared/under-maintenance/under-maintencance/under-maintencance.component';
import { LookerDashboardComponent } from './dashboard/looker-dashboard/looker-dashboard.component';
import { AssessmentUseCaseEnum } from './ai-assessments/models/assessments.enum';
import { GovTabsGuard } from './feature-flag/gov-feature-flag.guard';
import { AiPostureRouteFlagStrategy } from './ai-posture/guards/ai-posture-route-flag.strategy';
import { AiPostureAccessService } from './ai-posture/guards/ai-posture-access.service';
import { DsrUnderMaintencanceComponent } from './shared/under-maintenance/dsr-under-maintencance/dsr-under-maintencance.component';

const routes: Routes = [
	{
		path: RoutesManager.pageNotAllowed,
		canMatch: [ AuthGuard, CompanyVerificationGuard ],
		data: { disablePadding: true, showSidenav: false },
		component: MineNoPermissionsComponent
	},
	{
		path: RoutesManager.pageNotFound,
		data: { code: 404, disablePadding: true, showSidenav: false },
		component: ErrorPageComponent
	},
	{
		path: RoutesManager.underMaintenance,
		data: { disablePadding: true, showSidenav: false },
		component: UnderMaintencanceComponent
	},
	{
		path: RoutesManager.dsrUnderMaintenance,
		data: { disablePadding: true, showSidenav: true },
		component: DsrUnderMaintencanceComponent,
	},
	{
		path: RoutesManager.portalNoPermissions,
		data: { showHeader: true, disablePadding: true, showSidenav: false },
		component: PortalNoPermissionsComponent,
	},
	{
		path: `${RoutesManager.data_sources}/${RoutesManager.oauth_callback}`,
		data: { disablePadding: true, showSidenav: false },
        component: IntegrationOauthCallbackComponent
    },
	{
		path: RoutesManager.postCheckout,
		data: { disablePadding: true, showSidenav: false },
		component: PostCheckoutComponent
	},
	{
		path: RoutesManager.integrations,
		redirectTo: RoutesManager.data_sources,
		// We use prefix match to support redirect after integration's OAuth flow
		pathMatch: PrefixPathMatch
	},
	{
		path: RoutesManager.ai_assets,
		canMatch: [ AuthGuard, CompanyVerificationGuard, AssessmentsGuard, GovTabsGuard ],
		canActivate: [ FeatureFlagGuard, AssessmentsGuard ],
		data: { featureFlagName: FeatureFlags.DevAIDiscoveryPhase1 },
		loadChildren: async () => (await import('./ai-assets/ai-assets.module')).AiAssetsModule,
	},
	{
		path: RoutesManager.ai_agents,
		canMatch: [ AuthGuard, CompanyVerificationGuard, GovTabsGuard ],
		data: { preload: true },
		loadChildren: async () => (await import('./ai-agents/ai-agents.module')).AiAgentsModule,
	},
	{
		path: RoutesManager.ai_posture,
		canMatch: [ AuthGuard, CompanyVerificationGuard, UserRoleGuard, GovTabsGuard ],
		canActivate: [ FeatureFlagGuard ],
		// Reachable when the AISPM module is enabled OR with the DevAiPosturePrototype embed flag (see the strategy).
		data: { featureFlagName: AiPostureAccessService.CmsGateName, featureFlagStrategy: AiPostureRouteFlagStrategy, preload: true },
		loadChildren: async () => (await import('./ai-posture/ai-posture.module')).AiPostureModule,
	},
	{
		path: RoutesManager.risk_registry,
		canMatch: [ AuthGuard, CompanyVerificationGuard, GovTabsGuard ],
		canDeactivate: [LeavePageGuard],
		loadChildren: async () => (await import('./risks/risk-registry/risk-registry.module')).RiskRegistryModule,
	},
	{
		path: RoutesManager.risk_overview,
		canMatch: [ AuthGuard, CompanyVerificationGuard, GovTabsGuard ],
		data: { 
			type: LookerDashboardTypeEnum.Risk,
			dashboardUuid: DASHBOARD_UUIDS[LookerDashboardTypeEnum.Risk]
		},
		component: LookerDashboardComponent
	},
	{
		path: RoutesManager.assessments,
		canMatch: [ AuthGuard, CompanyVerificationGuard, AssessmentsGuard, GovTabsGuard ],
		canActivate: [ FeatureFlagGuard, AssessmentsGuard ],
		data: { preload: true, featureFlagName: FeatureFlags.DevAIAssessments },
		loadChildren: async () => (await import('./ai-assessments/assessments.module')).AssessmentsModule,
	},
	{
		path: RoutesManager.compliance_overview,
		canMatch: [ AuthGuard, CompanyVerificationGuard, GovTabsGuard ],
		data: { 
			type: LookerDashboardTypeEnum.Compliance,
			dashboardUuid: DASHBOARD_UUIDS[LookerDashboardTypeEnum.Compliance]
		},
		component: LookerDashboardComponent
	},
	{
		path: RoutesManager.governance_overview,
		canMatch: [ AuthGuard, CompanyVerificationGuard, GovTabsGuard ],
		data: { 
			type: LookerDashboardTypeEnum.GOV,
			dashboardUuid: DASHBOARD_UUIDS[LookerDashboardTypeEnum.GOV]
		},
		component: LookerDashboardComponent
	},
	{
		path: RoutesManager.ai_governance_overview,
		canMatch: [ AuthGuard, CompanyVerificationGuard, GovTabsGuard ],
		data: { 
			type: LookerDashboardTypeEnum.AiGov,
			dashboardUuid: DASHBOARD_UUIDS[LookerDashboardTypeEnum.AiGov]
		},
		component: LookerDashboardComponent
	},
	{
		path: RoutesManager.vendor_management_overview,
		canMatch: [ AuthGuard, CompanyVerificationGuard, GovTabsGuard ],
		data: { 
			type: LookerDashboardTypeEnum.TPRM,
			dashboardUuid: DASHBOARD_UUIDS[LookerDashboardTypeEnum.TPRM]
		},
		component: LookerDashboardComponent
	},
	{
		path: RoutesManager.systems,
		data: { preload: true },
		canMatch: [ AuthGuard, CompanyVerificationGuard, UserRoleGuard ],
		loadChildren: async() => (await import('./systems/systems.module')).SystemsModule,
	},
	{
		path: RoutesManager.dsr_overview,
		canMatch: [ AuthGuard, CompanyVerificationGuard, UserRoleGuard ],
		canActivate: [ DsrUnderMaintenanceGuard ],
		data: {
			type: LookerDashboardTypeEnum.DSR,
			dashboardUuid: DASHBOARD_UUIDS[LookerDashboardTypeEnum.DSR],
			featureFlag: FeatureFlags.ShutdownDSRTicketPages
		},
		component: LookerDashboardComponent
	},
	{
		path: RoutesManager.dsr_audit_log_overview,
		canMatch: [ AuthGuard, CompanyVerificationGuard, UserRoleGuard ],
		canActivate: [ DsrUnderMaintenanceGuard ],
		data: {
			type: LookerDashboardTypeEnum.DsrAuditLog,
			dashboardUuid: DASHBOARD_UUIDS[LookerDashboardTypeEnum.DsrAuditLog],
			featureFlag: FeatureFlags.ShutdownDSRTicketPages
		},
		component: LookerDashboardComponent
	},
	{
		path: RoutesManager.requests,
		data: { preload: true, featureFlag: FeatureFlags.ShutdownDSRTicketPages },
		canMatch: [ AuthGuard, CompanyVerificationGuard, UserRoleGuard ],
		canActivate: [ DsrUnderMaintenanceGuard ],
		loadChildren: async () => (await import('./requests/requests.module')).RequestsModule,
	},
	{
		path: RoutesManager.automations,
		redirectTo: `${RoutesManager.dsr_setup}/${RoutesManager.dsr_templates}`,
	},
	{
		path: RoutesManager.tasks,
		canMatch: [ AuthGuard, CompanyVerificationGuard ],
		loadChildren: async () => (await import('./tasks/tasks.module')).TasksModule,
	},
	{
		path: RoutesManager.dashboard,
		canMatch: [ AuthGuard, CompanyVerificationGuard, DashboardRoleGuard ],
		loadChildren: async () => (await import('./dashboard/dashboard.module')).DashboardModule,
	},
	{
		path: RoutesManager.profileHome,
		canMatch: [ AuthGuard, CompanyVerificationGuard ],
		loadChildren: async () => (await import('./profile/profile.module')).ProfileModule,
	},
	{
		path: RoutesManager.developers,
		canMatch: [ AuthGuard, CompanyVerificationGuard, UserRoleGuard ],
		loadChildren: async () => (await import('./developers/developers.module')).DevelopersModule,
	},
	{
		path: RoutesManager.plans,
		canMatch: [ AuthGuard, CompanyVerificationGuard ],
		loadChildren: async () => (await import('./plans/plans.module')).PlansModule,
	},
	{
		path: RoutesManager.privacyCenter,
		canMatch: [ AuthGuard, CompanyVerificationGuard, UserRoleGuard ],
		data: { preload: true },
		loadChildren: async () => (await import('./dynamic-privacy-center/dynamic-privacy-center.module')).DynamicPrivacyCenterModule
	},
	{
		path: RoutesManager.consentManagement,
		canMatch: [ AuthGuard, CompanyVerificationGuard, UserRoleGuard ],
		loadChildren: async () => (await import('./consent-management/consent-management.module')).ConsentManagementModule,
	},
	{
		path: RoutesManager.emailForwarding,
		data: { featureFlag: FeatureFlags.ShutdownDSRTicketPages },
		canMatch: [ AuthGuard, CompanyVerificationGuard, UserRoleGuard ],
		canActivate: [ DsrUnderMaintenanceGuard ],
		loadChildren: async () => (await import('./import-ticket/import-ticket.module')).ImportTicketModule,
	},
	{
		path: RoutesManager.onboarding,
		data: { changeBackground: true, disablePadding: true, showSidenav: false },
		loadChildren: async () => (await import('./onboarding/onboarding.module')).OnboardingModule,
	},
	{
		path: RoutesManager.homePage,
		component: HomeComponent,
		canMatch: [ AuthGuard, CompanyVerificationGuard, HomeGuard ],
		loadChildren: async () => (await import('./home/home.module')).HomeModule,
	},
	{
		// /employees lands inside the Radar shell (Software & vendors + Employees tabs).
		path: RoutesManager.employees,
		redirectTo: `${RoutesManager.radar}/${RoutesManager.radarEmployees}`,
		pathMatch: FullPathMatch,
	},
	{
		// Processing activities → RoPA page (PA-type assessments).
		path: RoutesManager.processingActivities,
		redirectTo: RoutesManager.ropa_assessments,
		pathMatch: FullPathMatch
	},
	{
		path: RoutesManager.ropa,
		redirectTo: RoutesManager.assessments,
		pathMatch: FullPathMatch
	},
	{
		path: RoutesManager.dataTypes,
		redirectTo: RoutesManager.dataClassifier,
		pathMatch: FullPathMatch
	},
	{
		path: RoutesManager.dataMappingLogin,
		data: { disablePadding: true, showSidenav: false },
		loadChildren: async() => (await import('./data-mapping-login/data-mapping-login.module')).DataMappingLoginModule,
	},
	{
		path: RoutesManager.companySettings,
		data: { preload: true },
		canMatch: [ AuthGuard, CompanyVerificationGuard, UserRoleGuard ],
		loadChildren: async() => (await import('./company-settings/company-settings.module')).CompanySettingsModule,
	},
	{
		path: RoutesManager.userSearch,
		data: { featureFlag: FeatureFlags.ShutdownDSRTicketPages },
		canMatch: [ AuthGuard, CompanyVerificationGuard, UserRoleGuard ],
		canActivate: [ DsrUnderMaintenanceGuard ],
		loadChildren: async() => (await import('./user-search/user-search.module')).UserSearchModule,
	},
	{
		path: RoutesManager.vendorManagement,
		data: { preload: true },
		canMatch: [ AuthGuard, CompanyVerificationGuard, UserRoleGuard, GovTabsGuard ],
		loadChildren: async() => (await import('./vendor-management/vendor-management.module')).VendorManagementModule,
	},
	{
		path: RoutesManager.policies,
		data: { preload: true },
		canMatch: [ AuthGuard, CompanyVerificationGuard, UserRoleGuard, GovTabsGuard ],
		loadChildren: async() => (await import('./policies/policies.module')).PoliciesModule,
	},
	{
		path: RoutesManager.dpoAdvisor,
		canMatch: [ AuthGuard, CompanyVerificationGuard, UserRoleGuard, GovTabsGuard ],
		data: { fullWidthMainContent: true },
		component: DpoAdvisorComponent,
		loadChildren: async() => (await import('./dpo-advisor/dpo-advisor.module')).DpoAdvisorModule,
	},
	{
		path: RoutesManager.impactAssessment,
		canActivate: [AssessmentsGuard],
		canMatch: [ AuthGuard, CompanyVerificationGuard, UserRoleGuard, AssessmentsGuard, GovTabsGuard ],
		loadChildren: async() => (await import('./impact-assessment/impact-assessment.module')).ImpactAssessmentModule,
	},
	{
		path: RoutesManager.discovery,
		canMatch: [ AuthGuard, CompanyVerificationGuard, UserRoleGuard, GovTabsGuard ],
		loadChildren: async() => (await import('./discovery-tools/discovery-tools.module')).DiscoveryToolsModule,
	},
	{
		// Radar is a shell (Software & vendors + Employees tabs).
		path: RoutesManager.radar,
		canMatch: [ AuthGuard, CompanyVerificationGuard, UserRoleGuard, GovTabsGuard ],
		data: { preload: true },
		loadChildren: async() => (await import('./radar-shell/radar-shell.module')).RadarShellModule,
	},
	{
		// Unified Risks shell (Risk registry + Business impact).
		path: RoutesManager.risks,
		canMatch: [ AuthGuard, CompanyVerificationGuard, UserRoleGuard, GovTabsGuard ],
		data: { preload: true },
		loadChildren: async() => (await import('./risks-shell/risks-shell.module')).RisksShellModule,
	},
	{
		// Unified Reporting page (dashboard cards).
		// No GovTabsGuard here: Reporting also hosts non-governance dashboards (DSR, DSR audit log,
		// Consent), so companies with `DoNotShowGovTabs` keep the page — only its governance cards and
		// their deep-links are hidden (reporting.component + ReportingDashboardRoleGuard).
		path: RoutesManager.reporting,
		canMatch: [ AuthGuard, CompanyVerificationGuard, UserRoleGuard ],
		loadChildren: async() => (await import('./reporting/reporting.module')).ReportingModule,
	},
	{
		path: RoutesManager.unusedAssets,
		canMatch: [ AuthGuard, CompanyVerificationGuard, UserRoleGuard, GovTabsGuard ],
		loadChildren: async() => (await import('./unused-assets/unused-assets.module')).UnusedAssetsModule,
	},
	{
		path: RoutesManager.dataClassifier,
		canMatch: [ AuthGuard, CompanyVerificationGuard, GovTabsGuard ],
		loadChildren: async() => (await import('./pii-classifier/pii-classifier.module')).PiiClassifierModule,
	},
	{
		path: RoutesManager.dsr_setup,
		canMatch: [ AuthGuard, CompanyVerificationGuard, UserRoleGuard ],
		data: { preload: true },
		loadChildren: async() => (await import('./dsr/dsr-setup/dsr-setup.module')).DsrSetupModule,
	},
	{
		path: RoutesManager.requests_ticket_copy_file_viewer,
		data: { showSidenav: false, disablePadding: true },
		loadChildren: async () => (await import('./dsr/dsr-ticket/dsr-ticket-content/dsr-ticket-processing/data-collection-processing/data-collection-file-viewer-v2/data-collection-file-viewer.module')).DataCollectionFileViewerV2Module,
	},
	{
		path: RoutesManager.datamapping,
		redirectTo: RoutesManager.dataMapping,
		pathMatch: FullPathMatch
	},
	{
		path: RoutesManager.piiClassifier,
		redirectTo: RoutesManager.dataClassifier,
	},
	{
		path: RoutesManager.thankYouPage,
		data: { disablePadding: true, showSidenav: false },
		component: ThankYouPageComponent,
	},
	{
		path: RoutesManager.cmp,
		canMatch: [ AuthGuard, CompanyVerificationGuard, UserRoleGuard ],
		loadChildren: async () => (await import('./cmp/cmp.module')).CmpModule,
	},
	{
		path: '',
		redirectTo: RoutesManager.homePage,
		pathMatch: FullPathMatch
	},
	{
		path: '**',
		redirectTo: RoutesManager.pageNotFound,
		pathMatch: FullPathMatch
	},
];

[ // Add split by use case assessments routes
	[RoutesManager.ai_assessments, AssessmentUseCaseEnum.AI],
	[RoutesManager.compliance_assessments, AssessmentUseCaseEnum.Privacy],
	[RoutesManager.ropa_assessments, AssessmentUseCaseEnum.Ropa],
	[RoutesManager.vendor_assessments, AssessmentUseCaseEnum.Vendor]
].forEach(([path, useCase]) => routes.unshift({
	path,
	data: {
		useCase,
		preload: true,
	},
	canActivate: [AssessmentsGuard],
	canMatch: [AuthGuard, CompanyVerificationGuard, AssessmentsGuard, GovTabsGuard],
	loadChildren: async () => (await import('./ai-assessments/assessments.module')).AssessmentsModule,
}));

@NgModule({
	imports: [
		RouterModule.forRoot(routes, {
			preloadingStrategy: CustomPreloadingStrategy,
			scrollPositionRestoration: 'top',
			anchorScrolling: 'enabled'
		}),
	],
	exports: [
		RouterModule
	],
	providers: [
		CustomPreloadingStrategy,
		{ provide: RouteReuseStrategy, useClass: CustomReuseStrategy }
	]
})
export class AppRoutingModule {}